import { useMsal } from '@azure/msal-react';
import { useNavigate, useParams } from 'react-router-dom';
import { useEffect, useRef, useState } from 'react';
import WizardForm from '../../components/forms/WizardForm';
import { WizardFormProps } from '../../components/forms/WizardForm/types';
import WizardStep from '../../components/forms/WizardForm/WizardStep';
import useBodyClass from '../../components/Utilities/useBodyClass';
import useAccountContext from '../../authentication/hooks';
import organisationAndContactProps from './organisationAndContactProps';
import InstrumentAndRequest from './instrumentAndRequest';
import instrumentAndRequestProps from './instrumentAndRequestProps';
import { FormStepStatusDto, RequestForQuoteClient } from '../../api/web-api-client';
import { tokenRequest } from '../../authentication/authConfig';
import BlockUISpinner from '../../components/BlockUISpinner';
import RequestForQuoteSummary from './requestForQuoteSummary';
import requestForQuoteSummaryProps from './requestForQuoteSummaryProps';
import OrganisationAndContact from './organisationAndContact';
import { AccountDetails } from '../../authentication/accountContext';
import AppLogger from '../../instrumentation/AppLogger';

const bannerTitle = 'Testing and calibration service - Request for quote';

const RequestForQuote = () => {
    const { accounts, instance } = useMsal();
    const { id } = useParams();
    const account = useAccountContext();
    const navigate = useNavigate();

    useBodyClass('wizard');

    const requestForQuoteWizardProps: WizardFormProps = {
        locationOnCompletion: `/request-for-quote-success/${id}`,
        lastStepNextButtonTitle: 'Submit',
        nextButtonTitle: 'Save and next',
        previousButtonTitle: 'Back',
        canSaveDraft: true,
        confirmationOnSubmission: {
            modalTitle: 'Are you sure you want to submit this request?',
            modalBodyText: `Once you have submitted this request,
        you will not be able to make any further changes in the Portal.`,
            noButtonTitle: 'No, go back',
            yesButtonTitle: 'Yes, submit',
        },
    };

    const isLoading = useRef(false);
    const [statuses, setStatuses] = useState<FormStepStatusDto[]>();

    useEffect(() => {
        const loadApplicationSteps = async () => {
            if (!statuses && accounts.length > 0) {
                const client = new RequestForQuoteClient();
                const tokenResult = await instance.acquireTokenSilent({
                    ...tokenRequest,
                    account: accounts[0],
                });
                client.setAuthToken(tokenResult.accessToken);
                try {
                    const result = await client.getStepStatuses(id!);
                    setStatuses(result);
                } catch (error) {
                    AppLogger.error('Failed to load application steps', error as Error, { Id: id });
                    navigate('/not-found');
                }
            }
        };
        if (!isLoading.current) {
            loadApplicationSteps();
        }
        return () => { isLoading.current = true; };
    }, [accounts, id, instance, isLoading, statuses]);

    const accountDetails : AccountDetails = account!.details!;
    return (
        !statuses
            ? (
                <BlockUISpinner>
                    <p>Loading...</p>
                </BlockUISpinner>
            )
            : (
                <WizardForm {...requestForQuoteWizardProps}>
                    <WizardStep {...organisationAndContactProps(id!, accounts, instance, accountDetails, statuses, bannerTitle)}>
                        <OrganisationAndContact name='' />
                    </WizardStep>
                    <WizardStep {...instrumentAndRequestProps(id!, accounts, instance, accountDetails, statuses, bannerTitle)}>
                        <InstrumentAndRequest name='' />
                    </WizardStep>
                    <WizardStep {...requestForQuoteSummaryProps(id!, accounts, instance, accountDetails, statuses, bannerTitle)}>
                        <RequestForQuoteSummary name='' />
                    </WizardStep>
                </WizardForm>
            ));
};

export default RequestForQuote;
